import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) };
}

async function apiFetch(path: string, options: RequestInit = {}) {
  const resp = await fetch(API_BASE + path, { headers: authHeaders(), ...options });
  if (!resp.ok) throw new Error('HTTP ' + resp.status);
  return resp.json();
}

export interface PurchaseOrderLine { id: string; itemCode: string; itemDescription: string; orderedQuantity: number; receivedQuantity: number; expectedDate: string | null; lineStatus: string; }
export interface PurchaseOrder { id: string; netsuiteId: string; poNumber: string; vendorName: string; status: string; orderDate: string; expectedReceiptDate: string | null; totalAmount: number; lines: PurchaseOrderLine[]; }
export interface SalesOrderLine { id: string; itemCode: string; itemDescription: string; orderedQuantity: number; shippedQuantity: number; requestedDate: string | null; committedDate: string | null; atpDate: string | null; lineStatus: string; }
export interface SalesOrder { id: string; netsuiteId: string; soNumber: string; customerName: string; status: string; orderDate: string; requestedShipDate: string | null; committedShipDate: string | null; priority: string; totalAmount: number; atpFeasible: boolean | null; lines: SalesOrderLine[]; }
export interface AtpResult { feasible: boolean; requestedDate: string; atpDate: string; daysDelta: number; constraintReason: string; supplyTimeline: any[]; demandTimeline: any[]; }
export interface ShippingAdvisoryItem { soId: string; soNumber: string; customerName: string; requestedShipDate: string; committedShipDate: string; daysDelta: number; atpFeasible: boolean; priority: string; }

interface SupplyChainState {
  purchaseOrders: { data: PurchaseOrder[]; total: number; loading: boolean; error: string | null };
  salesOrders: { data: SalesOrder[]; total: number; loading: boolean; error: string | null };
  shippingAdvisory: { data: ShippingAdvisoryItem[]; loading: boolean; error: string | null };
  atpQuery: { result: AtpResult | null; loading: boolean; error: string | null };
}

const initialState: SupplyChainState = {
  purchaseOrders: { data: [], total: 0, loading: false, error: null },
  salesOrders: { data: [], total: 0, loading: false, error: null },
  shippingAdvisory: { data: [], loading: false, error: null },
  atpQuery: { result: null, loading: false, error: null },
};

export const fetchPurchaseOrders = createAsyncThunk('supplyChain/fetchPOs', async (filters: any = {}) => {
  const params = new URLSearchParams(filters).toString();
  return apiFetch('/planning/supply-chain/purchase-orders' + (params ? '?' + params : ''));
});

export const fetchSalesOrders = createAsyncThunk('supplyChain/fetchSOs', async (filters: any = {}) => {
  const params = new URLSearchParams(filters).toString();
  return apiFetch('/planning/supply-chain/sales-orders' + (params ? '?' + params : ''));
});

export const fetchShippingAdvisory = createAsyncThunk('supplyChain/fetchAdvisory', async () => {
  return apiFetch('/planning/supply-chain/atp/shipping-advisory');
});

export const queryAtp = createAsyncThunk('supplyChain/queryAtp', async (params: { itemCode: string; quantity: number; requestedDate: string }) => {
  return apiFetch('/planning/supply-chain/atp/query', { method: 'POST', body: JSON.stringify(params) });
});

export const runAtpForSO = createAsyncThunk('supplyChain/runAtpForSO', async (soId: string) => {
  return apiFetch('/planning/supply-chain/atp/sales-order/' + soId, { method: 'POST', body: '{}' });
});

const supplyChainSlice = createSlice({
  name: 'supplyChain',
  initialState,
  reducers: { clearAtpResult: (state) => { state.atpQuery.result = null; state.atpQuery.error = null; } },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseOrders.pending, (state) => { state.purchaseOrders.loading = true; state.purchaseOrders.error = null; })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => { state.purchaseOrders.loading = false; state.purchaseOrders.data = action.payload.data || []; state.purchaseOrders.total = action.payload.total || 0; })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => { state.purchaseOrders.loading = false; state.purchaseOrders.error = action.error.message || 'Failed'; })
      .addCase(fetchSalesOrders.pending, (state) => { state.salesOrders.loading = true; state.salesOrders.error = null; })
      .addCase(fetchSalesOrders.fulfilled, (state, action) => { state.salesOrders.loading = false; state.salesOrders.data = action.payload.data || []; state.salesOrders.total = action.payload.total || 0; })
      .addCase(fetchSalesOrders.rejected, (state, action) => { state.salesOrders.loading = false; state.salesOrders.error = action.error.message || 'Failed'; })
      .addCase(fetchShippingAdvisory.pending, (state) => { state.shippingAdvisory.loading = true; state.shippingAdvisory.error = null; })
      .addCase(fetchShippingAdvisory.fulfilled, (state, action) => { state.shippingAdvisory.loading = false; state.shippingAdvisory.data = action.payload.data || []; })
      .addCase(fetchShippingAdvisory.rejected, (state, action) => { state.shippingAdvisory.loading = false; state.shippingAdvisory.error = action.error.message || 'Failed'; })
      .addCase(queryAtp.pending, (state) => { state.atpQuery.loading = true; state.atpQuery.error = null; state.atpQuery.result = null; })
      .addCase(queryAtp.fulfilled, (state, action) => { state.atpQuery.loading = false; state.atpQuery.result = action.payload.data; })
      .addCase(queryAtp.rejected, (state, action) => { state.atpQuery.loading = false; state.atpQuery.error = action.error.message || 'Failed'; });
  },
});

export const { clearAtpResult } = supplyChainSlice.actions;
export default supplyChainSlice.reducer;
